import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/base";
import { useAuth } from "../context/AuthContext";
import { formatSalonLabel } from "../utils/formatSalonLabel";

export default function UserList() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [roleFilter, setRoleFilter] = useState("all");
  const [salonFilter, setSalonFilter] = useState("all");

  const isSuperAdmin = Boolean(user?.is_super_admin);

  // Guard + initial load
  useEffect(() => {
    if (!token || !isSuperAdmin) {
      navigate("/");
      return;
    }
    loadData();
  }, [token, isSuperAdmin, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      // Load all users (admin-only) and all salons
      const [usersRes, salonsRes] = await Promise.all([
        fetch(`${API_BASE}/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE}/salons`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      const usersData = await usersRes.json();
      const salonsData = await salonsRes.json();

      if (!usersRes.ok) {
        setError(usersData.detail || "Unable to load users");
        return;
      }

      if (!salonsRes.ok) {
        console.error("Unable to load salons", salonsData);
      } else {
        setSalons(Array.isArray(salonsData) ? salonsData : []);
      }

      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error("Error loading users or salons", error);
      setError("Could not load users.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      const response = await fetch(`${API_BASE}/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 403) {
        alert("You are not allowed to delete users.");
        return;
      }

      if (response.status === 404) {
        alert("User not found.");
        return;
      }

      if (!response.ok) {
        alert(data.detail || "Delete failed");
        return;
      }

      alert("User deleted");
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (error) {
      console.error("Error deleting user", error);
      alert("Could not delete user.");
    }
  };

  // Map: owner_id -> [salons...]
  const salonsByOwnerId = useMemo(() => {
    const map = {};
    salons.forEach((s) => {
      if (!s.owner_id) return;
      if (!map[s.owner_id]) map[s.owner_id] = [];
      map[s.owner_id].push(s);
    });
    return map;
  }, [salons]);

  // Build salon dropdown options
  const salonOptions = useMemo(() => {
    const opts = [{ value: "all", label: "All salons" }];
    salons.forEach((s) => {
      const label = `${formatSalonLabel(s)}${s.city ? ` (${s.city})` : ""}`;
      opts.push({ value: String(s.id), label });
    });
    return opts;
  }, [salons]);

  // Apply role + salon filters to users list
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const userRole = (user.role || "").toLowerCase();

      if (roleFilter !== "all" && userRole !== roleFilter) {
        return false;
      }

      if (salonFilter !== "all") {
        const ownerSalons = salonsByOwnerId[user.id] || [];
        const ownsSelectedSalon = ownerSalons.some(
          (s) => String(s.id) === String(salonFilter)
        );
        if (!ownsSelectedSalon) return false;
      }

      return true;
    });
  }, [users, roleFilter, salonFilter, salonsByOwnerId]);

  if (loading) {
    return <p style={{ padding: "20px" }}>Loading users...</p>;
  }

  if (!isSuperAdmin) {
    return (
      <div style={{ maxWidth: "900px", margin: "30px auto", padding: "20px" }}>
        <h1 style={{ marginBottom: "16px" }}>Manage Users</h1>
        <p style={{ color: "red" }}>Only super admins can see this area.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "900px", margin: "30px auto", padding: "20px" }}>
      <h1 style={{ marginBottom: "16px" }}>Manage Users</h1>
      <p style={{ color: "#555", marginBottom: "18px" }}>
        Only super admins can see this area.
      </p>

      {error && (
        <div
          style={{
            marginBottom: "16px",
            padding: "10px 12px",
            borderRadius: "6px",
            background: "#ffe6e6",
            color: "#b00020",
          }}
        >
          {error}
        </div>
      )}

      {/* Add user button */}
      <div style={{ marginBottom: "16px", display: "flex", gap: "10px" }}>
        <button
          onClick={() => navigate("/users/create")}
          style={{
            padding: "10px 16px",
            background: "#2ecc71",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          + Add User
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "14px", fontWeight: 600 }}>Role:</span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="host">Host</option>
            <option value="technician">Technician</option>
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "14px", fontWeight: 600 }}>Salon:</span>
          <select
            value={salonFilter}
            onChange={(e) => setSalonFilter(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          >
            {salonOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <p style={{ color: "#777" }}>No users match the current filters.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f4f4f4" }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Role</th>
              <th style={thStyle}>Salons (as host)</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const userRole = (user.role || "").toLowerCase();
              const ownerSalons = salonsByOwnerId[user.id] || [];
              const salonNames =
                userRole === "host"
                  ? ownerSalons
                      .map((s) =>
                        s.city ? `${s.name} (${s.city})` : s.name
                      )
                      .join(", ")
                  : "";

              // Make sure the name is on a single line (strip newlines / extra spaces)
              const displayName = (user.full_name || "")
                .replace(/<br\s*\/?>/gi, " ")
                .replace(/\s+/g, " ")
                .trim();

              return (
                <tr key={user.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td
                    style={{
                      ...tdStyle,
                      whiteSpace: "nowrap", // keep name on one line
                    }}
                  >
                    {displayName}
                  </td>
                  <td style={tdStyle}>{user.email}</td>
                  <td style={tdStyle}>{user.role}</td>
                  <td style={tdStyle}>
                    {userRole === "host"
                      ? salonNames || "No salons yet"
                      : "—"}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={() => navigate(`/users/edit/${user.id}`)}
                        style={btnEdit}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        style={btnDelete}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "10px",
  borderBottom: "2px solid #ddd",
};

const tdStyle = {
  padding: "10px",
  verticalAlign: "top",
};

const btnBase = {
  padding: "6px 14px",
  fontSize: "12px",
  fontWeight: 600,
  borderRadius: "4px", // rectangular like other pages
  border: "none",
  cursor: "pointer",
  color: "white",
  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
};

const btnEdit = {
  ...btnBase,
  background: "#0d6efd", // blue, matches other Edit buttons
};

const btnDelete = {
  ...btnBase,
  background: "#dc3545", // red, matches other Delete buttons
};
